import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, ArrowLeft } from 'lucide-react';
import { useFlowProtection } from '../../../shared/hooks/useFlowProtection';

const FLOW_STORAGE_KEY = 'fromCompanySignup';

interface CompanyCheckEmailContentProps {
    email: string;
    companyName: string;
}

const CompanyCheckEmailContent: React.FC<CompanyCheckEmailContentProps> = ({ email, companyName }) => {
    const navigate = useNavigate();

    const handleBackToSignIn = () => {
        // Clear flow data when going back
        sessionStorage.removeItem(FLOW_STORAGE_KEY);
        navigate('/auth/login');
    };

    return (
        <div
            className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
            style={{ fontFamily: 'Manrope, sans-serif' }}
        >
            {/* Background */}
            <div className="absolute inset-0 bg-[#015FA6]" />

            {/* Decorative ellipse */}
            <div
                className="absolute pointer-events-none"
                style={{
                    width: '803px',
                    height: '793px',
                    top: '-362px',
                    right: '-100px',
                    borderRadius: '50%',
                    background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.8) 0%, rgba(116, 153, 191, 0.465) 41.83%, rgba(237, 242, 247, 0) 100%)',
                    transform: 'rotate(26.84deg)',
                }}
            />

            {/* Glowing orb */}
            <div className="absolute w-[400px] h-[400px] bottom-[-100px] left-[-100px] bg-[#4FA3E0] rounded-full blur-[100px] opacity-20 pointer-events-none" />

            {/* Content */}
            <div className="relative z-10 text-center text-white max-w-md mx-auto">
                {/* Logo */}
                <div className="mb-8">
                    <img
                        src="/branding/logo_blue.svg"
                        alt="Hush"
                        className="mx-auto cursor-pointer"
                        style={{
                            width: 'auto',
                            height: '50px',
                            objectFit: 'contain'
                        }}
                        onClick={() => navigate('/')}
                        onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            const parent = e.currentTarget.parentElement;
                            if (parent) {
                                parent.innerHTML = '<div style="font-size: 32px; font-weight: 700; color: white; font-family: Manrope;">Hush</div>';
                            }
                        }}
                    />
                </div>

                {/* Email Icon */}
                <div
                    className="mx-auto mb-8 animate-bounce"
                    style={{
                        width: '120px',
                        height: '120px',
                        borderRadius: '50%',
                        background: 'rgba(255, 255, 255, 0.15)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backdropFilter: 'blur(10px)',
                        animationDuration: '2s'
                    }}
                >
                    <div
                        style={{
                            width: '80px',
                            height: '80px',
                            borderRadius: '50%',
                            background: 'rgba(255, 255, 255, 0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <Mail size={36} strokeWidth={1.5} />
                    </div>
                </div>

                {/* Title */}
                <h1
                    className="mb-4"
                    style={{
                        fontFamily: 'Manrope',
                        fontWeight: 700,
                        fontSize: 'clamp(28px, 5vw, 36px)',
                        color: '#FFFFFF',
                        lineHeight: '1.2'
                    }}
                >
                    Check your email
                </h1>

                {/* Description */}
                <p
                    className="mb-6"
                    style={{
                        fontFamily: 'Manrope',
                        fontSize: 'clamp(14px, 2vw, 16px)',
                        color: 'rgba(255, 255, 255, 0.8)',
                        lineHeight: '1.6',
                        maxWidth: '380px',
                        margin: '0 auto'
                    }}
                >
                    We've sent a password setup link to
                </p>

                {/* Email display */}
                <div
                    className="mb-6 py-3 px-6 rounded-xl inline-block"
                    style={{
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(10px)'
                    }}
                >
                    <p
                        style={{
                            fontFamily: 'Manrope',
                            fontSize: '18px',
                            fontWeight: 600,
                            color: '#FFFFFF'
                        }}
                    >
                        {email}
                    </p>
                </div>

                {/* Company name */}
                <p
                    className="mb-8"
                    style={{
                        fontFamily: 'Manrope',
                        fontSize: '14px',
                        color: 'rgba(255, 255, 255, 0.7)',
                        lineHeight: '1.6'
                    }}
                >
                    for <strong>{companyName}</strong>
                </p>

                {/* Instructions */}
                <div
                    className="mb-10 p-5 rounded-xl text-left"
                    style={{
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(10px)'
                    }}
                >
                    <p
                        className="mb-3"
                        style={{
                            fontFamily: 'Manrope',
                            fontSize: '14px',
                            fontWeight: 600,
                            color: '#FFFFFF'
                        }}
                    >
                        Next steps:
                    </p>
                    <ol
                        className="space-y-2"
                        style={{
                            fontFamily: 'Manrope',
                            fontSize: '14px',
                            color: 'rgba(255, 255, 255, 0.8)',
                            paddingLeft: '20px'
                        }}
                    >
                        <li>Open the email and click "Set Your Password"</li>
                        <li>Create your secure password</li>
                        <li>Complete your company profile</li>
                    </ol>
                </div>

                {/* Note */}
                <p
                    className="mb-8"
                    style={{
                        fontFamily: 'Manrope',
                        fontSize: '13px',
                        color: 'rgba(255, 255, 255, 0.6)',
                        lineHeight: '1.5'
                    }}
                >
                    It might take a minute or two to arrive.<br />
                    Be sure to check your spam folder too.
                </p>

                {/* Back to Sign In Button */}
                <button
                    onClick={handleBackToSignIn}
                    className="flex items-center gap-2 mx-auto transition-colors hover:text-white"
                    style={{
                        background: 'none',
                        border: 'none',
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontSize: '14px',
                        fontFamily: 'Manrope',
                        fontWeight: 500,
                        cursor: 'pointer',
                        padding: '12px 0'
                    }}
                >
                    <ArrowLeft size={16} />
                    Back to sign in
                </button>
            </div>
        </div>
    );
};

/**
 * CompanyCheckEmail Page
 * Protected by useFlowProtection - only accessible after company signup flow
 * Direct URL access redirects to company signup page
 * Uses Session Storage to persist on page refresh (user might refresh while waiting for email)
 */
const CompanyCheckEmail: React.FC = () => {
    const { isValidAccess, flowData } = useFlowProtection({
        storageKey: FLOW_STORAGE_KEY,
        fallbackPath: '/auth/company/signup'
    });

    // Show nothing while checking or if invalid
    if (isValidAccess === null || isValidAccess === false) {
        return null;
    }

    const email = (flowData?.email as string) || 'your email';
    const companyName = (flowData?.companyName as string) || 'your company';

    return <CompanyCheckEmailContent email={email} companyName={companyName} />;
};

export default CompanyCheckEmail;
